import type { MewsBookingState } from '@/services/platform/data/booking/mews/types';
import type { EventCheckoutState, MewsCheckoutState } from './types';

const STORAGE_KEY = 'jncquoi.checkout';

type PersistedCheckout = ({ kind: 'event' } & EventCheckoutState) | ({ kind: 'mews' } & MewsCheckoutState);

/**
 * Short-lived copy of the booking being paid for.
 *
 * The booking normally travels as router state, which a full page load destroys. Two things do
 * exactly that: a redirect-based payment method sending the customer to an authorisation page and
 * back via `return_url`, and a plain refresh. Without a copy the screen would find no booking and
 * bounce to the home page mid-payment — with a PaymentIntent already created.
 *
 * `sessionStorage`, not `localStorage`: this is scoped to the tab and the trip, and must not outlive
 * it. Nothing sensitive is stored — ids, a guest count and a price, all of which the server
 * re-resolves and re-authorises anyway; the amount is never taken from here.
 *
 * One storage key for every context: only one checkout is ever in flight per customer, and the
 * `kind` tag on what's written is what stops a stale entry of one kind from being handed to the
 * other context's screen on recall.
 */
export function rememberCheckout(state: PersistedCheckout): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    // Private browsing and storage quotas can both refuse writes. The in-memory router state still
    // works for the common path, so this must not break the screen.
    console.warn('rememberCheckout: could not persist checkout state:', error);
  }
}

export function recallCheckout(kind: 'event'): EventCheckoutState | undefined;
export function recallCheckout(kind: 'mews'): MewsCheckoutState | undefined;
export function recallCheckout(kind: 'event' | 'mews'): EventCheckoutState | MewsCheckoutState | undefined {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as PersistedCheckout;
    if (parsed?.kind !== kind) return undefined;

    // Stripped of `kind` on the way out — callers ask for a specific kind and get back exactly
    // the shape they declared (`EventCheckoutState`/`MewsCheckoutState`), the tag having done its
    // one job of routing this call to the right branch.
    const { kind: _kind, ...state } = parsed;

    // The fields each context cannot function without; anything missing them is a partial or
    // stale write and is treated as absent.
    if (parsed.kind === 'event') {
      return parsed.eventId && parsed.returnPath ? state : undefined;
    }
    return parsed.reservationGuid && parsed.returnPath ? state : undefined;
  } catch (error) {
    console.warn('recallCheckout: could not read checkout state:', error);
    return undefined;
  }
}

/**
 * Which context's screen a recalled session belongs to, without validating its fields — used
 * only by `CheckoutScreenRouter` to pick an adapter when router state was lost entirely (a
 * redirect or a refresh). The adapter itself still calls `recallCheckout` and gets the
 * field-validated result.
 */
export function peekCheckoutKind(): 'event' | 'mews' | undefined {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<PersistedCheckout>;
    return parsed?.kind === 'event' || parsed?.kind === 'mews' ? parsed.kind : undefined;
  } catch {
    return undefined;
  }
}

/**
 * `dateFrom`/`dateTo` arrive as real `Date` instances when `booking` travels as live router
 * state, but as ISO strings once it has round-tripped through `sessionStorage` (recall) or a
 * checkout screen's own return-trip router state (still JSON-shaped, since React Router does not
 * revive it either). `new Date(x)` is a no-op for an already-real `Date` and a revive for a
 * string, so this is safe to call unconditionally regardless of where `booking` came from.
 */
export function reviveMewsBooking(booking: MewsBookingState): MewsBookingState {
  return {
    ...booking,
    dateFrom: booking.dateFrom ? new Date(booking.dateFrom) : null,
    dateTo: booking.dateTo ? new Date(booking.dateTo) : null,
  };
}

export function forgetCheckout(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — a stale entry is overwritten by the next checkout.
  }
}
